import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useUrlQuery } from 'hooks/useUrlQuery';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import useProfileQuestions from 'services/general/profileQuestions';
import useUpdateProfileAnswer from 'services/surveys/useUpdateProfileAnswer';
import useUpdateSampleInfoAnswer from 'services/surveys/useUpdateSampleInfoAnswer';
import useSampleInfoQuestions from 'services/surveys/useSampleInfoQuestions';
import { ProfileQuestionAnswer } from 'types/ProfileQuestions';
import { ContainerSizes, PageContainer, Typography } from 'components';
import { THSurvey } from '../Profile';
import { TextLink } from '@repo/ui';

export const ProfileEditGroup = () => {
  const flowType = useUrlQuery().get('flow');
  const { currentKit } = useCurrentKitAndTinyAccount();

  if (currentKit?.date_results_ready)
    return (
      <PageContainer
        size={ContainerSizes.SM}
        className='flex flex-column align-items-center gap-2 mt-4 p-2'
      >
        <TextLink
          label='Back to Home'
          to='/'
          iconNameLeft='chevronBack'
          size='body-s'
        />
        <Typography variant='heading-m'>Kit results are ready</Typography>
        <Typography variant='body-s'>
          You can no longer edit this kit's information.
        </Typography>
      </PageContainer>
    );
  return flowType === 'sampleinfo' ? <SampleInfoGroup /> : <ProfileGroup />;
};

export const ProfileGroup = () => {
  const { questionGroupId } = useParams<{ questionGroupId: string }>();
  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();
  const { data: profileQuestions } = useProfileQuestions(
    currentTinyAccountId,
    currentKitId,
  );
  const profileAnswerMutation = useUpdateProfileAnswer();

  const questionGroup = profileQuestions?.find(
    questionGroup => questionGroup.id === Number(questionGroupId),
  );

  if (!currentTinyAccountId || !questionGroup) return <></>;

  const mutationCallback = useCallback(
    (answers: ProfileQuestionAnswer[]) => {
      return profileAnswerMutation.mutateAsync({
        answers,
        tinyAccountId: currentTinyAccountId,
        kitId: currentKitId,
        isEditMode: true,
      });
    },
    [profileAnswerMutation],
  );

  return (
    <THSurvey
      tinyAccountId={currentTinyAccountId}
      questionGroups={[questionGroup]}
      mutationCallback={mutationCallback}
      modalProps={profileAnswerMutation.modalProps}
      isSingleEdit={true}
    />
  );
};

export const SampleInfoGroup = () => {
  const { questionGroupId } = useParams<{ questionGroupId: string }>();
  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();
  const { data: sampleInfoQuestions } = useSampleInfoQuestions(
    currentTinyAccountId,
    currentKitId,
  );
  const sampleInfoAnswerMutation = useUpdateSampleInfoAnswer();

  const questionGroup = sampleInfoQuestions?.find(
    questionGroup => questionGroup.id === Number(questionGroupId),
  );

  if (!currentTinyAccountId || !currentKitId || !questionGroup) return <></>;

  const mutationCallback = useCallback(
    (answers: ProfileQuestionAnswer[]) => {
      return sampleInfoAnswerMutation.mutateAsync({
        answers,
        tinyAccountId: currentTinyAccountId,
        kitId: currentKitId,
        isEditMode: true,
      });
    },
    [sampleInfoAnswerMutation],
  );

  return (
    <THSurvey
      tinyAccountId={currentTinyAccountId}
      questionGroups={[questionGroup]}
      mutationCallback={mutationCallback}
      modalProps={sampleInfoAnswerMutation.modalProps}
      isSingleEdit={true}
    />
  );
};
